import { lazy, ComponentType } from "react";

const RELOAD_KEY = "lovable:chunk-reloaded-at";

/**
 * React.lazy wrapper that recovers from stale build chunks.
 * After a new deploy, old chunk URLs 404 and the dynamic import rejects with
 * "Failed to fetch dynamically imported module" -> blank screen.
 * We retry once, then force a single hard reload to pick up the new index.html.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      // one silent retry (transient network hiccup)
      try {
        return await factory();
      } catch {
        const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
        // avoid reload loops: only reload once per minute
        if (Date.now() - last > 60_000) {
          sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
          window.location.reload();
          // never resolves; the page is going away
          return await new Promise<{ default: T }>(() => {});
        }
        throw error;
      }
    }
  });
}
